// Globale Schnellerfassung (UX-Vorschlag P2) — reine Auflöse-Logik ohne DOM.
//
// Aus dem geparsten Eingabetext (lib/quickAdd.ts, `extended`) wird hier das
// Ziel bestimmt, BEVOR angelegt wird — die Vorschau im CaptureDialog zeigt
// genau das Ergebnis dieser Funktionen:
//   - #Projekt  → Projektaufgabe; Phase = aktive bzw. erste offene, per ↹ wählbar.
//                 Passen mehrere Projekte, wird NICHT geraten: die Vorschau
//                 zeigt eine Auswahl.
//   - +Liste    → persönliche Aufgabe in dieser Liste.
//   - ohne beides → persönliche Standardliste (lib/defaultList.ts), wie heute.
//   - Uhrzeit   → Erinnerung zu Fälligkeitstag + Uhrzeit (POST /api/reminders);
//                 ohne Datum gilt heute. Aufgaben selbst haben keine Uhrzeit.
//
// Server unverändert: POST /api/phases/:id/tasks bzw. /api/personal-tasks.

import type { Phase, ProjectListItem, TaskList, UserPublic } from "@juuplan/shared";
import { resolveTargetList, writableLists } from "./defaultList";

/** Vergleichsform: klein, ohne Leer- und Satzzeichen (Umlaute bleiben). */
export function normalizeName(s: string): string {
  return s.toLocaleLowerCase("de-DE").replace(/[^\p{L}\p{N}]+/gu, "");
}

function words(s: string): string[] {
  return s
    .toLocaleLowerCase("de-DE")
    .split(/[^\p{L}\p{N}]+/u)
    .filter(Boolean);
}

/**
 * #-Token, das genau dieses Projekt trifft (für die Vorbelegung vom mobilen
 * +-Knopf, P7): Leerraum wird zu „-", übrige Zeichen außerhalb des Token-
 * Alphabets fallen weg; normalisiert ist es dann der exakte Projektname.
 */
export function projectToken(name: string): string {
  return captureToken("#", name);
}

/** Wie projectToken, mit frei wählbarem Präfix („#" Projekt, „+" Liste). */
export function captureToken(prefix: "#" | "+", name: string): string {
  const body = name.trim().replace(/\s+/g, "-").replace(/[^\p{L}\p{N}_.\-]/gu, "");
  // „+" braucht als erstes Zeichen einen Buchstaben (siehe LIST_RE).
  if (!body || (prefix === "+" && !/^\p{L}/u.test(body))) return "";
  return `${prefix}${body}`;
}

/**
 * Vorbelegung des mobilen +-Knopfs (P7) je Seite; null = auf dieser Seite
 * keinen Knopf zeigen.
 *   /p/:id      → „#Projekt" (nur wenn dort Aufgaben angelegt werden dürfen)
 *   /lists/:id  → „+Liste" (beschreibbar), sonst Standardliste
 *   /lists, /my-tasks → Standardliste („")
 *   /today      → „" und zusätzlich Eintrag in Mein Tag (MobileCaptureFab)
 */
export function fabPrefill(
  path: string,
  projects: ProjectListItem[],
  lists: TaskList[]
): string | null {
  const p = /^\/p\/(\d+)(?:\/|$)/.exec(path);
  if (p) {
    const project = capturableProjects(projects).find((x) => x.id === Number(p[1]));
    return project ? projectToken(project.name) : null;
  }
  const l = /^\/lists(?:\/([^/]+))?\/?$/.exec(path);
  if (l) {
    const id = l[1] ? Number(l[1]) : NaN;
    const list = Number.isFinite(id) ? writableLists(lists).find((x) => x.id === id) : undefined;
    return list ? captureToken("+", list.name) : "";
  }
  if (/^\/my-tasks\/?$/.test(path)) return "";
  if (/^\/today\/?$/.test(path)) return ""; // + Mein Tag, siehe MobileCaptureFab
  return null;
}

/**
 * Generischer Namensabgleich: exakter Treffer (normalisiert) gewinnt allein;
 * sonst alle, deren Name den Suchtext enthält oder bei denen ein Wort damit
 * beginnt. Reihenfolge der Eingabe bleibt erhalten.
 */
export function matchByName<T extends { name: string }>(items: T[], query: string): T[] {
  const q = normalizeName(query);
  if (!q) return [];
  const exact = items.filter((i) => normalizeName(i.name) === q);
  if (exact.length > 0) return exact;
  const ql = query.toLocaleLowerCase("de-DE");
  return items.filter(
    (i) => normalizeName(i.name).includes(q) || words(i.name).some((w) => w.startsWith(ql))
  );
}

/** Projekte, in denen der Benutzer Aufgaben anlegen darf (nicht abgeschlossen/archiviert). */
export function capturableProjects(projects: ProjectListItem[]): ProjectListItem[] {
  return projects.filter(
    (p) => p.my_role !== "viewer" && p.status !== "archived" && p.status !== "done"
  );
}

/**
 * Standardphase: die erste aktive (nach Reihenfolge), sonst die erste nicht
 * erledigte, sonst die erste überhaupt; null bei Projekten ohne Phase.
 */
export function defaultPhase(phases: Phase[]): Phase | null {
  const sorted = [...phases].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  return (
    sorted.find((p) => p.status === "active") ??
    sorted.find((p) => p.status !== "done") ??
    sorted[0] ??
    null
  );
}

/** Nächste Phase für ↹ (zyklisch, nach Reihenfolge). */
export function cyclePhase(phases: Phase[], currentId: number | null, dir: 1 | -1 = 1): Phase | null {
  const sorted = [...phases].sort((a, b) => a.sort_order - b.sort_order || a.id - b.id);
  if (sorted.length === 0) return null;
  const idx = sorted.findIndex((p) => p.id === currentId);
  if (idx < 0) return sorted[0]!;
  return sorted[(idx + dir + sorted.length) % sorted.length]!;
}

/**
 * @Name → Benutzer. Eindeutig nur bei genau einem Treffer (Teilstring im
 * Namen, wie QuickAddTask); sonst Liste der Kandidaten zur Anzeige.
 */
export function matchAssignee(users: UserPublic[], name: string): UserPublic[] {
  const lower = name.toLocaleLowerCase("de-DE");
  if (!lower) return [];
  return users.filter((u) => u.name.toLocaleLowerCase("de-DE").includes(lower));
}

export type CaptureTarget =
  | { kind: "project"; project: ProjectListItem }
  | { kind: "project-ambiguous"; query: string; candidates: ProjectListItem[] }
  | { kind: "project-none"; query: string }
  | { kind: "list"; list: TaskList; isDefault: boolean }
  | { kind: "list-ambiguous"; query: string; candidates: TaskList[] }
  | { kind: "list-none"; query: string }
  | { kind: "no-list" };

/**
 * Bestimmt das Ziel. `#` hat Vorrang vor `+` (eine Projektaufgabe hat keine
 * Liste). `pickedProjectId`/`pickedListId` ist die Auswahl des Benutzers bei
 * Mehrdeutigkeit — sie gilt nur, solange sie unter den Kandidaten ist.
 */
export function resolveCaptureTarget(input: {
  projectQuery?: string;
  listQuery?: string;
  projects: ProjectListItem[];
  lists: TaskList[];
  defaultListId: number | null;
  pickedProjectId?: number | null;
  pickedListId?: number | null;
}): CaptureTarget {
  if (input.projectQuery !== undefined) {
    const candidates = matchByName(capturableProjects(input.projects), input.projectQuery);
    if (candidates.length === 0) return { kind: "project-none", query: input.projectQuery };
    const picked = candidates.find((p) => p.id === input.pickedProjectId);
    if (picked) return { kind: "project", project: picked };
    if (candidates.length === 1) return { kind: "project", project: candidates[0]! };
    return { kind: "project-ambiguous", query: input.projectQuery, candidates };
  }
  if (input.listQuery !== undefined) {
    const candidates = matchByName(writableLists(input.lists), input.listQuery);
    if (candidates.length === 0) return { kind: "list-none", query: input.listQuery };
    const picked = candidates.find((l) => l.id === input.pickedListId);
    if (picked) return { kind: "list", list: picked, isDefault: false };
    if (candidates.length === 1) return { kind: "list", list: candidates[0]!, isDefault: false };
    return { kind: "list-ambiguous", query: input.listQuery, candidates };
  }
  const def = resolveTargetList(input.lists, input.defaultListId);
  return def ? { kind: "list", list: def, isDefault: true } : { kind: "no-list" };
}

/** Kann mit diesem Ziel angelegt werden? */
export function isTargetReady(t: CaptureTarget, phase: Phase | null): boolean {
  if (t.kind === "project") return phase !== null;
  return t.kind === "list";
}

/** Zuständige sind bei persönlichen Aufgaben nur auf geteilten Listen erlaubt (#854). */
export function listAllowsAssignee(list: TaskList): boolean {
  return list.scope === "space";
}

/**
 * Erinnerungszeitpunkt (ISO, UTC) aus Fälligkeitstag + Uhrzeit in LOKALER Zeit.
 * Ohne Datum gilt `todayIso`. null bei fehlender Uhrzeit.
 */
export function reminderIso(dueDate: string | undefined, time: string | undefined, todayIso: string): string | null {
  if (!time) return null;
  const day = dueDate ?? todayIso;
  const dm = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  const tm = /^(\d{2}):(\d{2})$/.exec(time);
  if (!dm || !tm) return null;
  const d = new Date(
    Number(dm[1]),
    Number(dm[2]) - 1,
    Number(dm[3]),
    Number(tm[1]),
    Number(tm[2])
  );
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

const WEEKDAY_SHORT = ["So", "Mo", "Di", "Mi", "Do", "Fr", "Sa"];

/** „Di 27.10.2026" bzw. „Di 27.10.2026, 10:00" für die Vorschau. */
export function formatDuePreview(dueDate: string | undefined, time: string | undefined, todayIso: string): string | null {
  if (!dueDate && !time) return null;
  const day = dueDate ?? todayIso;
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(day);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const base = `${WEEKDAY_SHORT[d.getDay()]} ${m[3]}.${m[2]}.${m[1]}`;
  return time ? `${base}, ${time}` : base;
}
